<template>
  <MetaHead :title="wtGeneral('navigation', 'summary')" />
  <ProjectHeader :meta="projectBase.projectMeta.value" />
  <div class="relative core-wrapper md:px-12 lg:px-24">
    <StepsIndicator />

    <!-- Stored-results banner with update button -->
    <div
      v-if="isStoredMode"
      class="stored-results-banner my-8 bg-yellow-50 border border-yellow-300 px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <span class="text-sm text-yellow-800">
        {{ $t('version.stored_results.banner_text', { date: storedResultsDate }) }}
      </span>
      <button
        v-if="canAcceptUpdate"
        class="shrink-0 h-9 px-4 text-sm core-action-button core-action-button--primary"
        @click="updateAndContinue">
        {{ $t('version.stored_results.update_button') }}
      </button>
    </div>

    <BaseSection id="summary" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
      <ProjectSimplifiedSummary
        :quantityArea="simplifiedData.quantityArea"
        :usages="simplifiedData.usages"
        :landscapeQuantityArea="simplifiedData.landscapeQuantityArea"
        :landscapeUsages="simplifiedData.landscapeUsages"
        :civilEngineerSlabArea="simplifiedData.civilEngineerSlabArea"
        :totalHours="simplifiedData.totalHours" />
    </BaseSection>

    <BaseSection id="download" :nextStep="showSubmissionForm" @onNextStepClick="projectBase.onNextStepClick">
      <ProjectSummaryDownload
        :lastCalculatedAt="lastCalculatedAt"
        :lastDownloadedAt="lastDownloadedAt"
        :entries="stateEntries"
        :canStorePdfs="canStorePdfs"
        :saving="savingPdf"
        :downloadingId="downloadingPdfId"
        :restoringId="restoringStateId"
        :busyId="busyStateId"
        @downloadProject="downloadProject"
        @downloadPdf="downloadStoredPdf"
        @restoreState="restoreStoredState"
        @renameState="renameStoredState"
        @deleteState="deleteStoredState" />
    </BaseSection>

    <ProjectStateNameModal
      :open="nameModal !== null"
      :title="nameModal?.title ?? ''"
      :description="nameModal?.description"
      :confirmLabel="nameModal?.confirmLabel ?? ''"
      :initialValue="nameModal?.initialValue"
      @confirm="nameModal?.resolve($event)"
      @close="nameModal?.resolve(null)" />

    <BaseSection v-if="showSubmissionForm" id="project-submission">
      <ProjectSubmissionForm />
    </BaseSection>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import BaseSection from '@/components/partials/layout/BaseSection.vue'
import MetaHead from '@/components/partials/display/MetaHead.vue'
import ProjectHeader from '@/components/partials/navigation/ProjectHeader.vue'
import StepsIndicator from '@/components/partials/navigation/StepsIndicator.vue'
import ProjectSimplifiedSummary from '@/components/project/summary/ProjectSimplifiedSummary.vue'
import ProjectSummaryDownload from '@/components/project/summary/ProjectSummaryDownload.vue'
import ProjectSubmissionForm from '@/components/project/summary/ProjectSubmissionForm.vue'
import ProjectStateNameModal from '@/components/partials/modals/ProjectStateNameModal.vue'
import { useI18n } from 'vue-i18n'
import { useProjectGeneralCalculator } from '@/stores/projectCenter/projectGeneralCalculator'
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectCivilEngineerCalculator,
  useProjectCivilEngineerExcavationCalculator,
  useProjectEngineerCalculator,
  useProjectElectricalCalculator,
  useProjectHeatingCoolingCalculator,
  useProjectVentilationACCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
  useProjectBuildingAutomationCalculator,
  useProjectSpecialistCoordinationCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'
import { PdfMakeSummaryGenerator } from '@/services/pdf/pdfmake-generator'
import { downloadProjectPdf, fetchProjectPdfs, uploadProjectPdf, type ProjectPdfItem } from '@/services/projectPdfs'
import {
  createProjectState,
  deleteProjectState,
  fetchProjectState,
  fetchProjectStates,
  renameProjectState,
  type ProjectStateItem,
} from '@/services/projectStates'
import { buildProjectStateEntries, type ProjectStateEntry } from '@/helpers/project-state-entries'
import { applyPreparedProjectLoad, prepareProjectLoad } from '@/helpers/project-state-loader'
import { useProjectTransferTexts } from '@/helpers/project-transfer-texts'
import projectPageBase from '@/base/projectPageBase'
import { useProjectStore } from '@/stores/project'
import { useContentStore } from '@/stores/content'
import { DialogEvent, Events, useDialogBus, useEventBus } from '@/helpers/event-bus'
import { renderGeneralSummary as renderGeneralSummaryPure } from '@/helpers/render-general-summary'
import { renderSimplifiedSummary } from '@/helpers/render-simplified-summary'
import { renderSetupSummary as renderSetupSummaryPure } from '@/helpers/render-setup-summary'
import { renderServiceSummary as renderServiceSummaryPure } from '@/helpers/render-service-summary'
import { getUsageDisplayTitle } from '@/helpers/usage-display'
import {
  buildStoredServiceResultsV2,
  isStoredServiceResultsV2,
  type StoredServiceResultsV2,
} from '@/helpers/service-results'
import { DEFAULT_COUNTRY } from '@arcexperts/core-types'
import { useClientStore } from '@/stores/client'
import { useClientFeatureFlags } from '@/helpers/client-feature-flags'
import type { SharedUsageItem, UsageCorrection } from '@/schemas/general/types'
import { formatTimestamp, formatTimestampWithTime } from '@/helpers/format'
import * as Sentry from '@sentry/vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import { trackPdfDownloaded, trackSummaryViewed } from '@/services/analytics/plausible'

const { t } = useI18n()
const { wt } = useWidgetText('widget-texts-project-general')
const { wt: wtSetup } = useWidgetText('widget-texts-project-setup')
const { wt: wtService } = useWidgetText('widget-texts-project-service')
const { wt: wtSummary } = useWidgetText('widget-texts-project-summary')
const { wt: wtGeneral } = useWidgetText('widget-texts-general')
declare const __APP_VERSION__: string

const router = useRouter()
const eventBus = useEventBus()
const dialogBus = useDialogBus()
const projectStore = useProjectStore()
const { preparedLoadErrorText } = useProjectTransferTexts()
const clientStore = useClientStore()
const contentStore = useContentStore()
const { showRiskSection, showServiceRoleSection } = useClientFeatureFlags()
const architectCalculator = useProjectArchitectCalculator()
const landscapeArchitectCalculator = useProjectLandscapeArchitectCalculator()
const generalCalculator = useProjectGeneralCalculator()
const civilEngineerCalculator = useProjectCivilEngineerCalculator()
const civilEngineerExcavationCalculator = useProjectCivilEngineerExcavationCalculator()
const engineerCalculator = useProjectEngineerCalculator()
const electricalCalculator = useProjectElectricalCalculator()
const heatingCoolingCalculator = useProjectHeatingCoolingCalculator()
const ventilationACCalculator = useProjectVentilationACCalculator()
const hvacCalculator = useProjectHVACCalculator()
const sanitaryCalculator = useProjectSanitaryCalculator()
const buildingAutomationCalculator = useProjectBuildingAutomationCalculator()
const specialistCoordinationCalculator = useProjectSpecialistCoordinationCalculator()

const projectBase = projectPageBase.useProjectPageBase()
const project = projectBase.project.value ?? {}
const projectMeta = computed(() => projectBase.projectMeta.value)
const projectMetaParts = computed<string[]>(() => (projectMeta.value?.parts as string[] | undefined) ?? [])
const projectValidRoles = computed<string[]>(() => (projectBase.projectValidRoles.value as string[] | undefined) ?? [])

const ROLE_WIDGET_TEXT_ROLES: Record<string, string> = {
  architect: 'ARCHITECT',
  landscape_architect: 'LANDSCAPE_ARCHITECT',
  civil_engineer: 'CIVIL_ENGINEER',
  civil_engineer_excavation: 'CIVIL_ENGINEER_EXCAVATION',
  electrical: 'ELECTRICAL',
  engineer: 'ENGINEER',
  heating_cooling: 'HEATING_COOLING',
  ventilation_ac: 'VENTILATION_AC',
  hvac: 'HVAC',
  sanitary: 'SANITARY',
  building_automation: 'BUILDING_AUTOMATION',
  specialist_coordination: 'SPECIALIST_COORDINATION',
}

const isStoredMode = computed(() => projectStore.useStoredResults)
const showSubmissionForm = computed(() => clientStore.show_submit_project_data && !isStoredMode.value)
const highestUpdateLevel = computed(() => projectStore.getHighestUpdateLevel)
const enrichedUpdates = computed(() => projectStore.getEnrichedUpdates)

// Can accept update only for PATCH/MINOR updates (not MAJOR)
const canAcceptUpdate = computed(() => {
  return highestUpdateLevel.value === 'patch' || highestUpdateLevel.value === 'minor'
})

if (!projectBase.project.value || !projectMeta.value) {
  Sentry.logger.warn('Project summary opened without ready project context', {
    hasProject: !!projectBase.project.value,
    hasProjectMeta: !!projectMeta.value,
  })
  void router.replace({ name: 'general-project-view' })
}

// Only init calculators in live mode
if (!isStoredMode.value) {
  projectBase.initCalculators()
}

// Reset stored mode when leaving the page
onUnmounted(() => {
  if (projectStore.useStoredResults) {
    projectStore.setUseStoredResults(false)
  }
})

// Redirect to update page when trying to navigate away in stored mode
onBeforeRouteLeave((to) => {
  if (isStoredMode.value && to.name !== 'version-update-view') {
    return { name: 'version-update-view' }
  }
  return true
})

// Accept updates and continue editing
function updateAndContinue() {
  const updates = enrichedUpdates.value
  if (updates) {
    for (const eu of updates) {
      projectStore.versionUpdate(eu.update.part, eu.update.version)
    }
  }
  projectStore.setUseStoredResults(false)
  router.push({ name: 'general-project-view' })
}

function getRoleWidgetText(slug: string, role: string, fallback: (...path: string[]) => string) {
  const widgetTextRole = ROLE_WIDGET_TEXT_ROLES[role]
  return (...path: string[]) => contentStore.getWidgetText(slug, widgetTextRole, ...path) ?? fallback(...path)
}

function getStoredSetupSummary(role: string) {
  const setupKey = `${role}_setup`
  const partData = project[setupKey]
  const results = partData?.calculatedResults?.results as Record<string, unknown> | undefined
  // SIA 105 usage split: the landscape architect derives from the landscape usage fields
  const isLandscapeRole = role === 'landscape_architect'
  const isCivilEngineerRole = role === 'civil_engineer'
  return renderSetupSummaryPure(
    {
      quantityArea: isLandscapeRole
        ? ((project.general?.data?.landscapeQuantityArea as number) ?? 0)
        : ((project.general?.data?.quantityArea as number) ?? 0),
      sharedUsages: isLandscapeRole
        ? ((project.general?.data?.landscapeUsageItems as SharedUsageItem[]) ?? [])
        : ((project.general?.data?.usageItems as SharedUsageItem[]) ?? []),
      isLandscapeArchitecture: isLandscapeRole,
      isCivilEngineer: isCivilEngineerRole,
      generalData: (project.general?.data as Record<string, unknown>) ?? null,
      corrections: (partData?.data?.usageCorrections as UsageCorrection[]) ?? [],
      // Fallback to old key for projects saved before the hours→minutes rename
      minutesPerSquareMeterForUsages:
        (results?.minutesPerSquareMeterForUsages as number[]) ??
        (results?.hoursPerSquareMeterForUsages as number[]) ??
        [],
      complexityForUsages: (results?.complexityForUsages as number[]) ?? [],
      estimatedBuildingCost: (results?.estimatedBuildingCost as number) ?? 0,
      risk: partData?.data?.risk as string | undefined,
      riskRemarks: partData?.data?.riskRemarks as string | undefined,
      buildingCost: partData?.data?.buildingCost as number | undefined,
      constructionCost: partData?.data?.constructionCost as number | undefined,
      resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
    },
    t,
    getRoleWidgetText('widget-texts-project-general', role, wt),
    getRoleWidgetText('widget-texts-project-setup', role, wtSetup),
    wtSummary
  )
}

function getStoredServiceSummary(role: string) {
  const setupKey = `${role}_setup`
  const serviceKey = `${role}_service`
  const serviceResults = project[serviceKey]?.calculatedResults?.results as Record<string, unknown> | undefined
  if (!isStoredServiceResultsV2(serviceResults)) {
    return {
      roleList: [],
      basicServiceList: [],
      additionalServiceList: [],
    }
  }

  return renderServiceSummaryPure(
    {
      country: (project.general?.data?.country as string) || DEFAULT_COUNTRY,
      focusTopics: (project.general?.data?.focusTopics as string[]) ?? [],
      serviceRole: project[setupKey]?.data?.serviceRole as string | null | undefined,
      serviceResults,
    },
    t,
    wt,
    getRoleWidgetText('widget-texts-project-service', role, wtService),
    getRoleWidgetText('widget-texts-project-setup', role, wtSetup),
    wtSummary
  )
}

const storedResultsDate = computed(() => {
  // Find the most recent calculatedAt across all parts
  let latest = 0
  for (const key of Object.keys(project)) {
    const calcAt = project[key]?.calculatedResults?.calculatedAt as number | undefined
    if (calcAt && calcAt > latest) latest = calcAt
  }
  return latest ? formatTimestamp(latest) : ''
})

const lastCalculatedAt = computed(() => {
  return (projectStore.getProject?.lastCalculatedAt as number | undefined) || undefined
})

const lastDownloadedAt = computed(() => {
  return (projectStore.getProject?.lastDownloadedAt as number | undefined) || undefined
})

const ROLES = [
  'architect',
  'landscape_architect',
  'civil_engineer',
  'civil_engineer_excavation',
  'engineer',
  'electrical',
  'heating_cooling',
  'ventilation_ac',
  'hvac',
  'sanitary',
  'building_automation',
  'specialist_coordination',
] as const

const projectSummaryValidParts = computed(() => {
  const validParts = ['general'] as string[]

  projectMetaParts.value.forEach((part) => {
    validParts.push(part?.toLowerCase() + '_setup')
  })
  projectValidRoles.value.forEach((role) => {
    validParts.push(role?.toLowerCase() + '_service')
  })

  return validParts
})

const projectSummary = {
  get title() {
    return projectBase.projectName.value || ''
  },
  get validParts() {
    return projectSummaryValidParts.value
  },
  lists: {
    general: computed(() => {
      const generalSummary = isStoredMode.value
        ? renderGeneralSummaryPure(
            { projectGeneral: project.general?.data as Record<string, unknown> | null },
            t,
            wt,
            wtSummary
          )
        : generalCalculator.renderGeneralSummary(t, wt, wtSummary)

      if (isStoredMode.value) {
        return {
          ...generalSummary,
          ...renderSimplifiedSummary(simplifiedData.value, t, wtSummary, wt),
        }
      }
      return {
        ...generalSummary,
        ...renderSimplifiedSummary(simplifiedData.value, t, wtSummary, wt),
      }
    }),
    ...Object.fromEntries(
      ROLES.flatMap((role) => [
        [
          `${role}_setup`,
          computed(() => {
            if (isStoredMode.value) return getStoredSetupSummary(role)
            return getCalculatorForRole(role).renderSetupSummary(
              t,
              getRoleWidgetText('widget-texts-project-general', role, wt),
              getRoleWidgetText('widget-texts-project-setup', role, wtSetup),
              wtSummary
            )
          }),
        ],
        [
          `${role}_service`,
          computed(() => {
            if (isStoredMode.value) return getStoredServiceSummary(role)
            return getCalculatorForRole(role).renderServiceSummary(
              t,
              wt,
              getRoleWidgetText('widget-texts-project-service', role, wtService),
              getRoleWidgetText('widget-texts-project-setup', role, wtSetup),
              wtSummary
            )
          }),
        ],
      ])
    ),
  },
}

function getCalculatorForRole(role: string) {
  switch (role) {
    case 'architect':
      return architectCalculator
    case 'landscape_architect':
      return landscapeArchitectCalculator
    case 'civil_engineer':
      return civilEngineerCalculator
    case 'civil_engineer_excavation':
      return civilEngineerExcavationCalculator
    case 'engineer':
      return engineerCalculator
    case 'electrical':
      return electricalCalculator
    case 'heating_cooling':
      return heatingCoolingCalculator
    case 'ventilation_ac':
      return ventilationACCalculator
    case 'hvac':
      return hvacCalculator
    case 'sanitary':
      return sanitaryCalculator
    case 'building_automation':
      return buildingAutomationCalculator
    case 'specialist_coordination':
      return specialistCoordinationCalculator
    default:
      return architectCalculator
  }
}

function getServiceResultsForRole(role: string): StoredServiceResultsV2 | null {
  const serviceKey = `${role}_service`

  if (isStoredMode.value) {
    const serviceResults = project[serviceKey]?.calculatedResults?.results
    return isStoredServiceResultsV2(serviceResults) ? serviceResults : null
  }

  const calculator = getCalculatorForRole(role)
  return buildStoredServiceResultsV2({
    estimatedTotalHours: calculator.estimatedTotalHours,
    phases: calculator.serviceData?.phases ?? [],
    percentageRangeForPhases: calculator.percentageRangeForPhases,
    serviceHours: calculator.serviceHours,
  })
}

function mapUsagesForSimplifiedSummary(usageList: SharedUsageItem[]) {
  return usageList.map((usage, index, allUsages) => ({
    title: getUsageDisplayTitle(
      usage as SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null },
      wt,
      {
        allUsages: allUsages as Array<
          SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null }
        >,
        index,
        resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
      }
    ),
    ratio: usage.ratio,
  }))
}

const hasLandscapePart = computed(() => projectMetaParts.value.includes('LANDSCAPE_ARCHITECT'))
const hasCivilEngineerPart = computed(() => projectMetaParts.value.includes('CIVIL_ENGINEER'))

const simplifiedData = computed(() => {
  const usages = mapUsagesForSimplifiedSummary(
    isStoredMode.value
      ? ((project.general?.data?.usageItems as SharedUsageItem[]) ?? [])
      : (generalCalculator.getSharedUsages as SharedUsageItem[])
  )

  // SIA 105 landscape architecture values - only shown when the project has the landscape role
  const landscapeUsages = hasLandscapePart.value
    ? mapUsagesForSimplifiedSummary(
        isStoredMode.value
          ? ((project.general?.data?.landscapeUsageItems as SharedUsageItem[]) ?? [])
          : (generalCalculator.getLandscapeSharedUsages as SharedUsageItem[])
      )
    : []

  const quantityArea = isStoredMode.value
    ? ((project.general?.data?.quantityArea as number) ?? 0)
    : generalCalculator.getQuantityArea

  const landscapeQuantityArea = hasLandscapePart.value
    ? isStoredMode.value
      ? ((project.general?.data?.landscapeQuantityArea as number) ?? 0)
      : generalCalculator.getLandscapeQuantityArea
    : undefined
  const civilEngineerSlabArea = hasCivilEngineerPart.value
    ? isStoredMode.value
      ? ((project.general?.data?.civilEngineerSlabArea as number | null) ?? undefined)
      : (generalCalculator.getCivilEngineerSlabArea ?? undefined)
    : undefined

  let totalHours = 0

  for (const part of projectMetaParts.value) {
    const role = part.toLowerCase()
    totalHours += getServiceResultsForRole(role)?.totals.totalHours ?? 0
  }

  return { quantityArea, usages, landscapeQuantityArea, landscapeUsages, civilEngineerSlabArea, totalHours }
})

// Stored-PDF state: PDFs are persisted in the CMS whenever the storage
// context (userId + refresh token, same requirements as the project mirror)
// is available; otherwise the button falls back to a direct download.
const storedPdfs = ref<ProjectPdfItem[]>([])
const storedStates = ref<ProjectStateItem[]>([])
const savingPdf = ref(false)
const downloadingPdfId = ref<string | null>(null)
const restoringStateId = ref<string | null>(null)
const busyStateId = ref<string | null>(null)
const nameModal = ref<{
  title: string
  description?: string
  confirmLabel: string
  initialValue: string
  resolve: (name: string | null) => void
} | null>(null)
const stateEntries = computed<ProjectStateEntry[]>(() => buildProjectStateEntries(storedStates.value, storedPdfs.value))

const pdfStorageContext = computed(() => {
  const userId = projectMeta.value?.userId
  const refreshToken = clientStore.refreshToken
  if (typeof userId === 'string' && userId.length > 0 && typeof refreshToken === 'string' && refreshToken.length > 0) {
    return { userId, refreshToken }
  }
  return null
})
const canStorePdfs = computed(() => pdfStorageContext.value !== null)

function getExistingProjectId(): string | null {
  const widgetMeta = projectStore.getProject?.widgetMeta as Record<string, unknown> | undefined
  return typeof widgetMeta?.projectId === 'string' ? widgetMeta.projectId : null
}

async function loadStoredStates() {
  const context = pdfStorageContext.value
  const projectId = getExistingProjectId()
  if (!context || !projectId) return
  // The lists are a convenience; keep the page usable when they cannot load.
  const [pdfs, states] = await Promise.all([
    fetchProjectPdfs(projectId, context.refreshToken).catch((error: unknown) => {
      Sentry.captureException(error, { tags: { operation: 'project_pdf_list' } })
      return null
    }),
    fetchProjectStates(projectId, context.refreshToken).catch((error: unknown) => {
      Sentry.captureException(error, { tags: { operation: 'project_state_list' } })
      return null
    }),
  ])
  if (pdfs) storedPdfs.value = pdfs
  if (states) storedStates.value = states
}

onMounted(() => {
  trackSummaryViewed()
  void loadStoredStates()
})

const downloadProject = async function () {
  const projectName = projectBase.projectName.value || 'unnamed'
  const partsCount = projectSummary.validParts.length
  const context = pdfStorageContext.value

  Sentry.metrics.count('pdf_download_click', 1, {
    attributes: {
      project_name: projectName,
      parts_count: String(partsCount),
      mode: context ? 'store' : 'download',
    },
  })

  let stateLabel: string | null = null
  if (context) {
    stateLabel = await askForStateName({
      title: t('project.summary.state.name_title'),
      description: t('project.summary.state.name_description'),
      confirmLabel: t('project.summary.state.name_confirm'),
      initialValue: t('project.summary.state.default_name', {
        number: storedStates.value.filter((state) => state.source === 'pdf').length + 1,
      }),
    })
    if (stateLabel === null) return
  }

  try {
    const pdfMakeSummaryGenerator = new PdfMakeSummaryGenerator(project, projectSummary, wtSummary, wtGeneral, wt, {
      showRiskSection: showRiskSection.value,
      showRoleSection: showServiceRoleSection.value,
    })

    if (context) {
      savingPdf.value = true
      const { blob, filename } = await pdfMakeSummaryGenerator.generateBlob()
      const projectId = projectStore.ensureProjectId()
      if (!projectId) throw new Error('missing_project_id')

      const created = await uploadProjectPdf(
        { projectId, userId: context.userId, filename, blob },
        context.refreshToken
      )
      storedPdfs.value = [created, ...storedPdfs.value]

      // Store the project state together with the PDF so the project can be
      // reset to exactly this state later. The PDF stays usable if this fails.
      try {
        const state = await createProjectState(
          {
            projectId,
            userId: context.userId,
            source: 'pdf',
            meta: (projectStore.getMeta ?? {}) as Record<string, unknown>,
            project: JSON.parse(JSON.stringify(projectStore.getProject ?? {})) as Record<string, unknown>,
            label: stateLabel,
            appVersion: __APP_VERSION__,
            pdfId: created.id,
          },
          context.refreshToken
        )
        storedStates.value = [state, ...storedStates.value]
      } catch (stateError) {
        Sentry.captureException(stateError, { tags: { operation: 'project_state_save' } })
        eventBus.emit(Events.SHOW_ERROR, { key: 'project.summary.state.save_error' })
      }

      Sentry.metrics.count('pdf_save_success', 1, {
        attributes: {
          project_name: projectName,
          filename: created.name,
        },
      })
    } else {
      const filename = await pdfMakeSummaryGenerator.generate()

      projectStore.setLastDownloadedAt()
      trackPdfDownloaded()

      Sentry.metrics.count('pdf_download_success', 1, {
        attributes: {
          project_name: projectName,
          filename: filename,
        },
      })
    }
  } catch (error) {
    Sentry.metrics.count(context ? 'pdf_save_error' : 'pdf_download_error', 1, {
      attributes: {
        project_name: projectName,
        error_type: error instanceof Error ? error.name : 'unknown',
      },
    })
    Sentry.captureException(error)
    eventBus.emit(Events.SHOW_SYSTEM_ERROR, {
      key: context ? 'project.summary.pdf.save_error' : 'project.summary.pdf.error',
      err: error instanceof Error ? error.message : String(error),
    })
  } finally {
    savingPdf.value = false
  }
}

function askForStateName(options: {
  title: string
  description?: string
  confirmLabel: string
  initialValue: string
}): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    nameModal.value = {
      ...options,
      resolve: (name) => {
        nameModal.value = null
        resolve(name)
      },
    }
  })
}

function confirmDialog(title: string, text: string, confirm: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    dialogBus.emit(DialogEvent.SHOW_CONFIRM, {
      title,
      text,
      confirm,
      cancel: t('general.button.cancel'),
    })
    dialogBus.on(DialogEvent.DONE, (confirmed: boolean) => {
      dialogBus.off(DialogEvent.DONE)
      resolve(confirmed)
    })
  })
}

async function restoreStoredState(entry: ProjectStateEntry) {
  const context = pdfStorageContext.value
  if (!context || !entry.stateId || restoringStateId.value) return

  restoringStateId.value = entry.stateId
  try {
    const state = await fetchProjectState(entry.stateId, context.refreshToken)
    const prepared = await prepareProjectLoad(state.project, state.meta, state.timestamp)

    if (!prepared.ok) {
      dialogBus.emit(DialogEvent.SHOW_DIALOG, {
        title: t('project.summary.state.restore_blocked_title'),
        text: preparedLoadErrorText(prepared),
      })
      return
    }

    const date = formatTimestampWithTime(new Date(state.createdAt).getTime())
    const hint = prepared.compatibleUpdates.length
      ? ` ${t('general.transfer.import.warnings.version_auto_update')}`
      : ''
    const confirmed = await confirmDialog(
      t('project.summary.state.restore_title'),
      `${t('project.summary.state.restore_confirm', { date })}${hint}`,
      t('project.summary.state.restore_confirm_button')
    )
    if (!confirmed) return

    // Keep the current state recoverable: store it as an automatic backup and
    // only reset once that succeeded.
    const projectId = getExistingProjectId()
    if (!projectId) throw new Error('missing_project_id')
    const backup = await createProjectState(
      {
        projectId,
        userId: context.userId,
        source: 'backup',
        meta: (projectStore.getMeta ?? {}) as Record<string, unknown>,
        project: JSON.parse(JSON.stringify(projectStore.getProject ?? {})) as Record<string, unknown>,
        label: t('project.summary.state.backup_label'),
        appVersion: __APP_VERSION__,
      },
      context.refreshToken
    ).catch((error: unknown) => {
      Sentry.captureException(error, { tags: { operation: 'project_state_backup' } })
      return null
    })
    if (!backup) {
      eventBus.emit(Events.SHOW_ERROR, { key: 'project.summary.state.backup_error' })
      return
    }
    storedStates.value = [backup, ...storedStates.value]

    const applied = await applyPreparedProjectLoad(prepared)
    if (!applied) throw new Error('project_state_not_applied')

    eventBus.emit(Events.SHOW_SUCCESS, { key: 'project.summary.state.restored' })
    Sentry.metrics.count('project_state_restored', 1, { attributes: { source: state.source } })
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'project_state_restore' } })
    eventBus.emit(Events.SHOW_ERROR, { key: 'project.summary.state.restore_error' })
  } finally {
    restoringStateId.value = null
  }
}

async function renameStoredState(entry: ProjectStateEntry) {
  const context = pdfStorageContext.value
  if (!context || !entry.stateId || busyStateId.value) return

  const name = await askForStateName({
    title: t('project.summary.state.rename_title'),
    confirmLabel: t('project.summary.state.rename_confirm'),
    initialValue: entry.label || entry.pdf?.name || '',
  })
  if (name === null || name === entry.label) return

  busyStateId.value = entry.stateId
  try {
    const updated = await renameProjectState(entry.stateId, name, context.refreshToken)
    storedStates.value = storedStates.value.map((item) => (item.id === updated.id ? updated : item))
    eventBus.emit(Events.SHOW_SUCCESS, { key: 'project.summary.state.renamed' })
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'project_state_rename' } })
    eventBus.emit(Events.SHOW_ERROR, { key: 'project.summary.state.rename_error' })
  } finally {
    busyStateId.value = null
  }
}

async function deleteStoredState(entry: ProjectStateEntry) {
  const context = pdfStorageContext.value
  if (!context || !entry.stateId || busyStateId.value) return

  const confirmed = await confirmDialog(
    t('project.summary.state.delete_title'),
    t('project.summary.state.delete_confirm', {
      name: entry.label || entry.pdf?.name || t('project.summary.state.default_label'),
      date: formatTimestampWithTime(new Date(entry.createdAt).getTime()),
    }),
    t('project.summary.state.delete_confirm_button')
  )
  if (!confirmed) return

  busyStateId.value = entry.stateId
  try {
    await deleteProjectState(entry.stateId, context.refreshToken)
    storedStates.value = storedStates.value.filter((item) => item.id !== entry.stateId)
    if (entry.pdf) storedPdfs.value = storedPdfs.value.filter((item) => item.id !== entry.pdf!.id)
    eventBus.emit(Events.SHOW_SUCCESS, { key: 'project.summary.state.deleted' })
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'project_state_delete' } })
    eventBus.emit(Events.SHOW_ERROR, { key: 'project.summary.state.delete_error' })
  } finally {
    busyStateId.value = null
  }
}

async function downloadStoredPdf(pdf: ProjectPdfItem) {
  const context = pdfStorageContext.value
  if (!context) return

  downloadingPdfId.value = pdf.id
  try {
    const blob = await downloadProjectPdf(pdf.id, context.refreshToken)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = pdf.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    projectStore.setLastDownloadedAt()
    trackPdfDownloaded()

    // The CMS stamps lastDownloadedAt on the download request; mirror it
    // locally so the list updates without a refetch.
    const downloadedAt = new Date().toISOString()
    storedPdfs.value = storedPdfs.value.map((item) =>
      item.id === pdf.id ? { ...item, lastDownloadedAt: downloadedAt } : item
    )

    Sentry.metrics.count('pdf_download_success', 1, {
      attributes: {
        project_name: projectBase.projectName.value || 'unnamed',
        filename: pdf.name,
      },
    })
  } catch (error) {
    Sentry.metrics.count('pdf_download_error', 1, {
      attributes: {
        project_name: projectBase.projectName.value || 'unnamed',
        error_type: error instanceof Error ? error.name : 'unknown',
      },
    })
    Sentry.captureException(error)
    eventBus.emit(Events.SHOW_SYSTEM_ERROR, {
      key: 'project.summary.pdf.error',
      err: error instanceof Error ? error.message : String(error),
    })
  } finally {
    downloadingPdfId.value = null
  }
}
</script>

<style lang="postcss" scoped>
.stored-results-banner {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2),
    0 3px 7px -7px color-mix(in srgb, #facc15 20%, transparent);
}
</style>
